import React from 'react';
import { useTheme } from 'styled-components';
import { TableRow } from '@material-ui/core';
import { useTranslation } from 'react-i18next';
import { isNumber } from 'lodash/fp';
import { DEFAULT_TABLE_ROW_HEIGHT, HeadCell, LoaderRow, TableCell } from '../../../../ui-kit';
import { PaginatedInventoryCountItemsQuery } from '../../../../generated/graphql';
import { usePriceService } from '../../../../utils/hooks/priceService';
import { useUser } from '../../../../utils/hooks/auth';
import RealValuationCell from './RealValuationCell';
import { InventoryCountDetailsRow, getInvnetoryCountDetailRowColor } from './utils';

interface RowProps {
    headCells: HeadCell<InventoryCountDetailsRow>[];
    row: InventoryCountDetailsRow;
}

const Row = ({ headCells, row }: RowProps) => {
    const { t } = useTranslation();
    const theme = useTheme();
    const backgroundColor = getInvnetoryCountDetailRowColor(row, theme);

    return (
        <TableRow style={{ backgroundColor }} tabIndex={-1}>
            {headCells.map(({ id, align }) => {
                const isFirstElement = id === 'name';
                let cellValue: React.ReactNode = row[id];
                const { expectedValuationNumber, realValuationNumber } = row;
                if (id === 'realValuation' && row.realValuation && isNumber(expectedValuationNumber) && isNumber(realValuationNumber)) {
                    if (realValuationNumber > expectedValuationNumber) {
                        cellValue = (<RealValuationCell variant="success">{cellValue}</RealValuationCell>);
                    }

                    if (realValuationNumber < expectedValuationNumber) {
                        cellValue = <RealValuationCell variant="error">{cellValue}</RealValuationCell>;
                    }
                }

                if ((id === 'realValuation' && !row.realValuation) || (id === 'realCount' && !row.realCount)) {
                    cellValue = t('Not calculated');
                }

                const component = isFirstElement ? 'th' : 'td';

                return (
                    <TableCell
                        component={component}
                        isSticky={isFirstElement}
                        key={id} id={id} align={align}
                    >
                        {cellValue}
                    </TableCell>
                );
            })}
        </TableRow>
    );
};

interface InventoryCountTableContentProps {
    inventoryCountItems: PaginatedInventoryCountItemsQuery['paginatedInventoryCountItems']['items'];
    headCells: HeadCell<InventoryCountDetailsRow>[];
    isLoading: boolean;
    rowsPerPage: number;
}

const InventoryCountTableContent = ({ headCells, isLoading, inventoryCountItems, rowsPerPage }: InventoryCountTableContentProps) => {
    const { priceService } = usePriceService();
    const { user: { defaultCurrency } } = useUser();
    const { t } = useTranslation();

    const rows: Array<InventoryCountDetailsRow> = inventoryCountItems.map(({
        id,
        expectedCount,
        pricePerUnitWithoutVAT,
        stockCard,
        realCount,
    }) => {
        const expectedValuation = priceService.multiply(
            pricePerUnitWithoutVAT,
            expectedCount,
            defaultCurrency.currencyCode,
            { onlyPositiveQuantity: false },
        );
        const realValuation = isNumber(realCount)
            ? priceService.multiply(pricePerUnitWithoutVAT, realCount, defaultCurrency.currencyCode)
            : null;

        return ({
            id,
            name: stockCard.name,
            categoryName: stockCard.stockCardCategory?.name || t('Unassigned'),
            expectedValuation: priceService.toLocaleString(expectedValuation, defaultCurrency.currencyCode),
            expectedValuationNumber: expectedValuation,
            realValuation: isNumber(realValuation)
                ? priceService.toLocaleString(realValuation, defaultCurrency.currencyCode)
                : null,
            realValuationNumber: realValuation,
            expectedCount: `${expectedCount} ${stockCard.storageUnit}`,
            realCount: isNumber(realCount) ? `${realCount} ${stockCard.storageUnit}` : null,
        });
    });

    return (
        <>
            {isLoading
                ? <LoaderRow columnCount={headCells.length} itemsPerPage={rowsPerPage} itemHeight={DEFAULT_TABLE_ROW_HEIGHT} />
                : rows.map(row => (
                    <Row
                        key={row.id}
                        row={row}
                        headCells={headCells}
                    />
                ))}
        </>
    );
};

export default InventoryCountTableContent;
